import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import SearchBar from "@/components/SearchBar";
import { fetchProperties } from "@/lib/api";
import { Star, ArrowUpRight } from "lucide-react";

// Italic accent helper — Vela's editorial cadence is "short sentences, one
// phrase italicised". Keep the typography (Cormorant accent face) intact and
// only switch the case treatment.
const Em = ({ children }) => (
  <span className="italic" style={{ fontFamily: "var(--font-accent)" }}>
    {children}
  </span>
);

const VelaHome = () => {
  const [props, setProps] = useState([]);
  useEffect(() => { fetchProperties({ brand: "vela" }).then(setProps); }, []);

  return (
    <div data-testid="vela-home">
      {/* HERO — full-bleed editorial photo grounded in actual Vela geography.
         The Sellin Seebrücke (Rügen, Baltic coast) viewed from the shore: the
         pier complex with its white pavilion towers reads right of centre.
         A soft directional gradient — keyed to Vela's primary deep teal
         (`hsl(195 50% 25%)`) — is applied to the left third of the frame and
         fades to transparent across the centre, giving the typography a quiet
         deeper backdrop without altering the photograph itself. The right
         two-thirds where the pier architecture sits stay unaltered. Hero
         typography on this side flips to Vela's warm cream (`#F7F0E2`) so it
         reads cleanly against the dusk-tinted backdrop. */}
      <section className="relative overflow-hidden" data-testid="vela-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1705475898645-7a1601b5cb2d?w=2400&q=85&auto=format"
            alt="The Sellin Seebrücke pier complex with its white pavilions over a calm Baltic Sea — Rügen, Germany"
            className="w-full h-full object-cover object-[78%_center]"
          />
          {/* Soft deep-teal directional gradient — calibrated to give the
              eyebrow, headline, sub-copy and search bar a uniform dusk-tinted
              backdrop without flattening the photograph. Dense on the left,
              holds through the sub-copy region (~0–55%), then tapers and
              fades to transparent past ~78% so the pier pavilion + dive-bell
              tower + Baltic blue on the right stay photograph. */}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(31, 78, 95, 0.66) 0%, rgba(31, 78, 95, 0.60) 22%, rgba(31, 78, 95, 0.48) 40%, rgba(31, 78, 95, 0.30) 56%, rgba(31, 78, 95, 0.10) 75%, rgba(31, 78, 95, 0.00) 100%)",
            }}
          />
          {/* Faint top darken — gives the eyebrow a touch of separation from
              the sky band above without reading as a panel. */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-1/3"
            style={{
              background:
                "linear-gradient(180deg, rgba(31, 78, 95, 0.18) 0%, rgba(31, 78, 95, 0.00) 100%)",
            }}
          />
          {/* Soft bottom darken under the search bar — keeps it anchored
              against the lower edge of the photograph. */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-1/3"
            style={{
              background:
                "linear-gradient(180deg, rgba(31, 78, 95, 0.00) 0%, rgba(31, 78, 95, 0.22) 100%)",
            }}
          />
        </div>
        <div className="relative container-rail pt-16 md:pt-28 pb-16 md:pb-24">
          <BrandLabel withRule className="text-[#F7F0E2]/85">The Vela Hotels &amp; Apartments · Est. 2019</BrandLabel>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <BrandHeading level={1} className="mt-6 max-w-[14ch] text-[#F7F0E2]">
              The <Em>other</Em> home.
            </BrandHeading>
          </motion.div>
          <div className="mt-10">
            <SearchBar scope="vela" defaultBrand="vela" />
          </div>
        </div>
      </section>

      {/* Five retreats */}
      <section className="bg-background" data-testid="vela-grid">
        <div className="container-rail py-16 md:py-24">
          <div className="text-center">
            <BrandLabel withRule>The Vela Coast</BrandLabel>
            {/* Rewrite #1 — was "five waterfront retreats. one slower register of the coast."
               Now: more substance, more specificity. */}
            <BrandHeading level={2} className="mt-4 max-w-[28ch] mx-auto">
              Five houses along the Baltic. <Em>One quieter way to meet the coast.</Em>
            </BrandHeading>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {props.map((p) => (
              <BrandCard key={p.id} className="group overflow-hidden" data-testid={`vela-card-${p.id}`}>
                <Link to={`/vela/p/${p.id}`} className="block relative aspect-[4/3] overflow-hidden">
                  <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                </Link>
                <div className="p-5">
                  <BrandLabel>Vela · {p.location}</BrandLabel>
                  <div className="mt-2 font-display text-[24px]">{p.name}</div>
                  <p className="mt-2 text-[14px] text-muted-foreground font-body line-clamp-2">{p.tagline}</p>
                  <div className="mt-4 flex items-center justify-between text-[12px]">
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Star size={12} className="fill-current" /> {p.rating?.toFixed(1)}
                    </span>
                    <Link
                      to={`/vela/p/${p.id}`}
                      className="text-[11px] tracking-wider text-[hsl(var(--primary))] underline-offset-4 hover:underline"
                      data-testid={`vela-cta-${p.id}`}
                    >
                      Discover {p.name.split(" ").slice(-1)[0]} →
                    </Link>
                  </div>
                </div>
              </BrandCard>
            ))}
          </div>
        </div>
      </section>

      {/* The Vela feeling */}
      <section className="bg-secondary" data-testid="vela-feeling">
        <div className="container-rail py-16 md:py-24">
          <div className="text-center">
            <BrandLabel withRule>The Vela Feeling</BrandLabel>
            {/* Rewrite #2 — was "a quirky kind of resort. kept simple in every house."
               "Quirky" was the wrong word. Replaced with restraint + consistency. */}
            <BrandHeading level={2} className="mt-4 max-w-[28ch] mx-auto">
              A different kind of coastal stay. <Em>The same calm in every house.</Em>
            </BrandHeading>
            <p className="mt-4 max-w-[60ch] mx-auto text-muted-foreground font-body leading-relaxed">
              Different buildings, different geographies. Three things every Vela house holds in common.
            </p>
          </div>
          <div className="mt-12 space-y-12">
            {[
              {
                l: "01 — Architecture",
                t: "Light-filled spaces.",
                b: "White-washed walls, high airy ceilings, soft natural light — each Vela house plays to the sky.",
                img: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1800&q=80",
              },
              {
                l: "02 — Materials",
                t: "Sea and sand tones.",
                b: "A muted palette taken from the coast itself — a sandy linen, a soft glass green, an oat-coloured wool.",
                img: "https://images.unsplash.com/photo-1540518614846-7eded433c457?w=1800&q=80",
              },
              {
                l: "03 — The Rituals",
                t: "The absolute view.",
                b: "Long mornings, slow dinners, and a steady supply of cold water — every Vela house keeps the same register of welcome.",
                img: "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1800&q=80",
              },
            ].map((f, i) => (
              <div key={f.t} className={`grid md:grid-cols-2 gap-8 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                <img src={f.img} alt={f.t} className="w-full aspect-[4/3] object-cover rounded-[10px]" style={{ direction: "ltr" }} />
                <div style={{ direction: "ltr" }}>
                  <BrandLabel>{f.l}</BrandLabel>
                  <div className="mt-3 font-display text-[28px] md:text-[36px]">{f.t}</div>
                  <p className="mt-4 text-[15px] md:text-[16px] text-muted-foreground font-body leading-relaxed editorial-measure">
                    {f.b}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dark section: parts of the day that aren't the bedroom */}
      <section className="bg-primary text-primary-foreground" data-testid="vela-rooms-of-the-day">
        <div className="container-rail py-16 md:py-24">
          <BrandLabel className="text-primary-foreground/70" withRule>Beyond the Room</BrandLabel>
          <BrandHeading level={2} className="mt-4 max-w-[26ch] text-primary-foreground">
            The <Em>parts</Em> of the day that <Em>aren't</Em> the bedroom.
          </BrandHeading>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <div className="border border-primary-foreground/20 p-6">
              <div className="font-display text-[20px] italic" style={{ fontFamily: "var(--font-accent)" }}>The Kitchen</div>
              <p className="mt-3 text-[13px] text-primary-foreground/70">
                House bread, baked twice a day. Mornings open with a soft simple breakfast spread; nights close with a small-plate kitchen for those who didn't go out.
              </p>
            </div>
            <div className="border border-primary-foreground/20 p-6">
              <div className="font-display text-[20px] italic" style={{ fontFamily: "var(--font-accent)" }}>The Library</div>
              <p className="mt-3 text-[13px] text-primary-foreground/70">
                Field guides and slow magazines, all selected by the host. A small lending shelf you take a book from at one house and leave at the next.
              </p>
            </div>
            <div className="border border-primary-foreground/20 p-6">
              <div className="font-display text-[20px] italic" style={{ fontFamily: "var(--font-accent)" }}>The Spa</div>
              <p className="mt-3 text-[13px] text-primary-foreground/70">
                Sauna, steam, a cold plunge and a long shower bench. Not every house has a spa, but every house with a sauna has a long bench.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notes from the coast */}
      <section className="bg-background" data-testid="vela-notes">
        <div className="container-rail py-16 md:py-24">
          <div className="text-center">
            <BrandLabel withRule>Stories from the Coast</BrandLabel>
            <BrandHeading level={2} className="mt-4 max-w-[26ch] mx-auto">
              Notes <Em>from the</Em> coast.
            </BrandHeading>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { t: "A cycling route along the dark peninsula.", img: "https://images.unsplash.com/photo-1471506480208-91b3a4cc78be?w=1400&q=80" },
              { t: "The season for amber, kept by the wind.",   img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1400&q=80" },
              { t: "A slow afternoon at the boatyard.",         img: "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1400&q=80" },
            ].map((n) => (
              <BrandCard key={n.t} className="overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={n.img} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                </div>
                <div className="p-5">
                  <BrandLabel>Journal</BrandLabel>
                  <div className="mt-2 font-display text-[22px]">{n.t}</div>
                  <Link
                    to="/vela/search"
                    className="mt-4 inline-flex items-center gap-1 text-[11px] tracking-wider hover:text-[hsl(var(--primary))]"
                  >
                    Read More <ArrowUpRight size={11} />
                  </Link>
                </div>
              </BrandCard>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VelaHome;
